import { useCallback } from "react";
import { useLocation } from "react-router-dom";

import { NavigationItemType, SubMenuItemType, NestedSubMenuItemType } from "../types";

export const useNavigationActiveState = () => {
  const location = useLocation();
  const currentPath = location.pathname;

  /**
   * Check if a specific href matches the current path
   */
  const isHrefActive = useCallback(
    (href: string | undefined): boolean => {
      if (!href || href === "#") {
        return false;
      }

      // Handle external links
      if (href.startsWith("http://") || href.startsWith("https://")) {
        return false;
      }

      // Extract the relevant part of the current path (after workspace ID)
      const pathParts = currentPath.split("/");
      const workspaceIndex = pathParts.findIndex(part => part === "workspace");
      if (workspaceIndex === -1 || workspaceIndex + 1 >= pathParts.length) {
        return false;
      }

      // Get path after workspace ID
      const relevantPath = pathParts.slice(workspaceIndex + 2).join("/");

      // Normalize href (remove leading slash if present)
      const normalizedHref = href.startsWith("/") ? href.slice(1) : href;

      // Check for exact match or if current path starts with the href
      return relevantPath === normalizedHref || relevantPath.startsWith(normalizedHref + "/");
    },
    [currentPath],
  );

  /**
   * Check if a nested sub-item is active
   */
  const isNestedSubItemActive = useCallback(
    (item: NestedSubMenuItemType): boolean => isHrefActive(item.href),
    [isHrefActive],
  );

  /**
   * Check if a sub-item or any of its nested items are active
   */
  const isSubItemActive = useCallback(
    (item: SubMenuItemType): boolean => {
      // Check if the sub-item itself is active
      if (isHrefActive(item.href)) {
        return true;
      }

      // Check if any nested items are active
      if (item.items && item.items.length > 0) {
        return item.items.some(isNestedSubItemActive);
      }

      return false;
    },
    [isHrefActive, isNestedSubItemActive],
  );

  /**
   * Check if a navigation item or any of its descendants are active
   */
  const isNavigationItemActive = useCallback(
    (item: NavigationItemType): boolean => {
      // Check main href
      if (isHrefActive(item.href)) {
        return true;
      }

      // Check alternative hrefs
      if (item.altHrefs?.some(isHrefActive)) {
        return true;
      }

      // Check all sub-items recursively
      if (item.items && item.items.length > 0) {
        return item.items.some(isSubItemActive);
      }

      return false;
    },
    [isHrefActive, isSubItemActive],
  );

  /**
   * Special handling for Settings items
   */
  const isSettingsItemActive = useCallback(
    (item: NavigationItemType | SubMenuItemType): boolean => {
      const path = currentPath;

      const pathParts = path.split("/");
      const settingsIndex = pathParts.findIndex(part => part === "settings");

      if (settingsIndex === -1) {
        return false;
      }

      if (item.name === "Settings") {
        return true;
      }

      if (!item.href) {
        return false;
      }

      const settingsPath = pathParts.slice(settingsIndex).join("/");
      const itemPath = item.href;

      // Check exact match or path prefix
      if (settingsPath === itemPath) {
        return true;
      }

      // Check nested items for SubMenuItemType
      if ("items" in item && item.items && item.items.length > 0) {
        return item.items.some(subItem => {
          const subItemPath = subItem.href;
          return settingsPath === subItemPath || settingsPath.startsWith(subItemPath + "/");
        });
      }

      return false;
    },
    [currentPath],
  );

  /**
   * Main function to determine if any item is active
   */
  const isItemActive = useCallback(
    (item: NavigationItemType | SubMenuItemType | NestedSubMenuItemType): boolean => {
      // Special handling for Settings paths
      if (item.href?.includes("settings") || ("name" in item && item.name === "Settings")) {
        return isSettingsItemActive(item as NavigationItemType | SubMenuItemType);
      }

      // Check if it's a NavigationItemType (has icon as function)
      if ("icon" in item && typeof item.icon === "function") {
        return isNavigationItemActive(item as NavigationItemType);
      }

      // Check if it's a SubMenuItemType with items
      if ("items" in item && item.items) {
        return isSubItemActive(item as SubMenuItemType);
      }

      // Simple item - just check href
      return isHrefActive(item.href);
    },
    [isNavigationItemActive, isSubItemActive, isSettingsItemActive, isHrefActive],
  );

  return {
    isItemActive,
  };
};
